import { memo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { MiDetailFields, MiFileUpload, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  FILE_MIME_TYPE_EXTENSIONS,
  INPUT_TYPES,
} from "@miplatform/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiFileDocumentOutline } from "@mdi/js";
import { useTheme } from "@mui/material";
import { Box } from "@mui/system";
import {
  PREBILL_PAYMENT_METHOD_OPTIONS,
  PREBILL_PAYMENT_NUMBER_LABEL_MAP,
} from "../prebillConstants";
import { usePrebillPaymentInformationModalViewModel } from "./usePrebillPaymentInformationModalViewModel";

const paymentInformationSchema = z.object({
  type: z.string().min(1, "Payment Method is required."),
  amount: z.number().min(1, "Amount is required."),
  number: z.string().min(1, "Field is required."),
});

export const PrebillPaymentInformationModal = memo(
  ({ open, attempt, invoice, length, onClose, onRefresh = () => {} }) => {
    const theme = useTheme();
    const {
      handleSubmit,
      control,
      reset,
      watch,
      setValue,
      getValues,
      formState: { isValid },
    } = useForm({
      mode: "onChange",
      defaultValues: {},
      resolver: zodResolver(paymentInformationSchema),
    });
    const {
      isLoading,
      file,
      documentCategory,
      getDocumentCategory,
      handleFileChange,
      resetFile,
      createBillingCommitment,
    } = usePrebillPaymentInformationModalViewModel({
      attempt,
      invoice,
      length,
      onClose: () => {
        reset();
        onClose();
      },
      onRefresh,
    });
    const type = watch("type");
    const fields = [
      {
        label: "Payment Method",
        name: "type",
        required: true,
        formControl: control,
        fieldType: INPUT_TYPES.SELECT,
        options: PREBILL_PAYMENT_METHOD_OPTIONS,
        width: "50%",
      },
      {
        label: "Amount",
        name: "amount",
        fieldType: INPUT_TYPES.CURRENCY,
        required: true,
        value: getValues("amount"),
        formControl: control,
        width: "50%",
        setValue: (rawValue) => {
          setValue(`amount`, Number(rawValue), {
            shouldValidate: true,
            shouldDirty: true,
          });
        },
      },
      {
        label: PREBILL_PAYMENT_NUMBER_LABEL_MAP[type],
        name: "number",
        required: true,
        formControl: control,
        width: "100%",
        style: { display: type ? "block" : "none" },
      },
    ];

    const handleClose = () => {
      onClose();
      reset();
      resetFile();
    };

    useEffect(() => {
      getDocumentCategory();
    }, []);

    return (
      <MiModal
        title="Add Payment Information"
        description="Enter information for the payment"
        open={open}
        closeModal={handleClose}
        titleIcon={mdiFileDocumentOutline}
        leftIconColor={theme.palette.primary.main}
        loading={isLoading}
        width={448}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            size: "medium",
            color: BUTTON_COLORS.BLACK,
            onClick: handleClose,
          },
          {
            name: "Add Payment Information",
            variant: BUTTON_VARIANTS.CONTAINED,
            size: "medium",
            disabled: isLoading || !isValid || !file || file.error,
            onClick: handleSubmit(createBillingCommitment),
          },
        ]}
      >
        <MiDetailFields detailFields={fields} />
        <Box px={1.5} pt={1}>
          <MiFileUpload
            showUploadButton
            dragAndDrop
            multiple={false}
            disabled={!documentCategory}
            validFileTypes={documentCategory?.allowedFileFormats}
            onChange={(files) => handleFileChange(files)}
            onDelete={resetFile}
            acceptableFormatText={`Acceptable formats: ${documentCategory?.allowedFileFormats
              .map((mimeType) => FILE_MIME_TYPE_EXTENSIONS[mimeType])
              .join(", ")}`}
          />
        </Box>
      </MiModal>
    );
  }
);
